export const navigationLinks = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why-choose" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const heroContent = {
  headline: "Packed Dance Floors. Seamless Wedding Nights. A Celebration Your Guests Never Forget.",
  subheadline:
    "Lobben Event Co. delivers high energy, expertly coordinated DJ experiences with expert music curation across every genre, so you can relax, celebrate, and trust that every moment flows flawlessly.",
  primaryCTA: "Check Availability",
  secondaryCTA: "Get a Quote",
  stats: [
    { number: "500+", label: "Events" },
    { number: "98%", label: "Satisfaction" },
    { number: "12+", label: "Years" },
  ],
};

export const valuePropositions = [
  {
    icon: "CalendarCheck",
    title: "A Perfectly Orchestrated Wedding",
    description:
      "From our first conversation to the final song of the night, every detail is planned with intention. Your reception flows naturally so you can focus on celebrating while we handle the music, timing, and energy of the night.",
  },
  {
    icon: "Heart",
    title: "A Night That Feels Like You",
    description:
      "No cookie cutter playlists here. We take the time to understand your style, your crowd, and your vision, so your wedding sounds and feels exactly the way you want it to.",
  },
  {
    icon: "ShieldCheck",
    title: "Trusted, Professional Execution",
    description:
      "On time. Prepared. Dialed in. You can relax knowing your event is being handled by a professional who takes your day seriously and delivers with confidence and polish.",
  },
  {
    icon: "Users",
    title: "An Unforgettable Guest Experience",
    description:
      "Your guests won't just hear great music. They'll feel the energy. We focus on creating moments that keep the dance floor full and the room buzzing all night long.",
  },
];

export const whyChoosePillars = [
  {
    icon: "Flame",
    title: "Energy & Flow",
    description:
      "We don't just play music. We engineer the energy of your event. Building momentum, reading the crowd, and creating peak moments that your guests will talk about for years.",
    highlight: "Your dance floor, always packed",
  },
  {
    icon: "Shuffle",
    title: "Adaptability & Music Versatility",
    description:
      "Every crowd is different. We bring a vast music library spanning decades and genres, adapting on the fly to keep every generation on the floor and every moment perfectly scored.",
    highlight: "Every genre, every generation",
  },
  {
    icon: "ArrowRightLeft",
    title: "Flawless Mixing & Transitions",
    description:
      "No awkward silences. No jarring switches. Our beat matched transitions and carefully planned setlists ensure your event flows like one continuous, unforgettable experience.",
    highlight: "Flawless from start to finish",
  },
  {
    icon: "Shield",
    title: "A Professional Experience From Start to Finish",
    description:
      "From setup to final breakdown, every detail is handled with professionalism and precision. Timeline coordination, vendor communication, and event flow are managed so your celebration runs smoothly from beginning to end.",
    highlight: "Relax, we've got this",
  },
];

export const services = [
  {
    title: "Weddings",
    description:
      "Our signature service. From intimate ceremonies to grand celebrations, we create the perfect sonic landscape for the most important day of your life. Every moment choreographed, every transition flawless.",
    features: [
      "Custom playlist curation",
      "Ceremony & reception coverage",
      "MC services included",
      "Timeline coordination",
      "Lighting packages available",
    ],
    image: "wedding",
    badge: "Premium",
    isPrimary: true,
  },
  {
    title: "Corporate Events",
    description:
      "Elevate your corporate gatherings with professional entertainment that matches your brand's prestige. From galas to product launches, we set the right tone.",
    features: [
      "Brand appropriate playlists",
      "Professional MC services",
      "Audio equipment provided",
      "Flexible packages",
    ],
    image: "corporate",
    isPrimary: false,
  },
  {
    title: "Private Parties",
    description:
      "Birthdays, anniversaries, milestones, whatever you're celebrating, we bring the energy and expertise to make it extraordinary.",
    features: [
      "Customized music selection",
      "Party planning support",
      "Sound & lighting setup",
      "All genre expertise",
    ],
    image: "private",
    isPrimary: false,
  },
];

export const testimonials = [
  {
    name: "Sarah & Jake",
    role: "Wedding Couple",
    event: "Wedding Reception",
    content:
      "Hiring Lobben Event Co. was hands down one of the best decisions we made for our wedding. Daren made the entire night feel effortless for us. The dance floor was packed from the first song to the last, and our guests are still talking about how much fun they had. We didn't have to worry about a single thing. He had everything handled.",
    rating: 5,
  },
  {
    name: "Lauren Mitchell",
    role: "Wedding Coordinator",
    event: "Luxury Wedding",
    content:
      "As a coordinator, I work with a lot of DJs, and Daren stands out for his professionalism and calm, confident approach. The timeline ran smoothly, the energy stayed high, and the couple was completely at ease. He's exactly the kind of DJ you want on your vendor team.",
    rating: 5,
  },
  {
    name: "Michael Torres",
    role: "Director of Events",
    event: "Corporate Gala",
    content:
      "We booked Lobben Event Co. for a corporate event, and the experience was flawless from start to finish. Professional, organized, and incredibly easy to work with. The music was perfect for our crowd, and the dance floor stayed busy all night. We've already recommended Daren to other teams.",
    rating: 5,
  },
  {
    name: "Jessica R.",
    role: "Private Event Host",
    event: "Birthday Celebration",
    content:
      "I wanted a fun, high energy party without having to manage anything myself, and that's exactly what I got. Daren took care of everything, kept the vibe perfect, and somehow got everyone dancing. It was effortless, smooth, and easily the best party we've thrown.",
    rating: 5,
  },
  {
    name: "Chris & Angela",
    role: "Happy Clients",
    event: "Wedding",
    content:
      "Professional, fun, and completely effortless. The dance floor was packed all night. We couldn't have asked for a better DJ experience.",
    rating: 5,
  },
  {
    name: "Lauren Whitaker",
    role: "Venue Director",
    event: "Wedding Venue",
    content:
      "Lobben Event Co. is one of the most organized DJs we work with. Setup was effortless, the timeline ran perfectly, and the dance floor stayed full all night. Couples love working with Daren because they know everything will run smoothly.",
    rating: 5,
  },
];

export const aboutContent = {
  title: "The Story Behind the Sound",
  paragraphs: [
    "Lobben Event Co. was built on a simple belief: every celebration deserves a soundtrack that's as unique and unforgettable as the people at the center of it.",
    "What started as a passion for music and live events has grown into a premium DJ service trusted by hundreds of couples, planners, and event organizers. We've built our reputation on one thing: making every event feel effortless for our clients while delivering an experience their guests never forget.",
    "We specialize in weddings because we understand the weight of the moment. Your wedding day isn't just another event. It's a once in a lifetime celebration, and the music should reflect that. From the first dance to the last song, we're there to ensure everything flows perfectly.",
  ],
  stats: [
    { number: "500+", label: "Events Performed" },
    { number: "12+", label: "Years Experience" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "50+", label: "5 Star Reviews" },
  ],
};

export const ctaContent = {
  headline: "Your Perfect Night Starts Here",
  subheadline:
    "Don't leave the most important moments of your celebration to chance. Let's create a celebration your guests will talk about for years.",
  primaryCTA: "Check Availability",
  secondaryCTA: "Request a Quote",
  urgency: "2026 dates are filling quickly. Inquire for availability.",
};

export const eventTypes = [
  "Wedding",
  "Corporate Event",
  "Private Party",
  "Anniversary",
  "Birthday Celebration",
  "Other",
];

export const footerLinks = {
  services: [
    { label: "Weddings", href: "#services" },
    { label: "Corporate Events", href: "#services" },
    { label: "Private Parties", href: "#services" },
  ],
  company: [
    { label: "About", href: "#about" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Contact", href: "#contact" },
  ],
};

export const images = {
  hero: "https://images.unsplash.com/photo-1708569177111-40b155d78801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHx3ZWRkaW5nJTIwZGFuY2UlMjBmbG9vcnxlbnwwfHx8fDE3NzE1MzE4NzZ8MA&ixlib=rb-4.1.0&q=85",
  wedding:
    "https://images.pexels.com/photos/6266941/pexels-photo-6266941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  corporate:
    "https://images.pexels.com/photos/6405751/pexels-photo-6405751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  private:
    "https://images.unsplash.com/photo-1544155892-b2b6c64204fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=85",
  about:
    "https://images.unsplash.com/photo-1618107095181-e3ba0f53ee59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxESiUyMHdlZGRpbmd8ZW58MHx8fHwxNzcxNTMxODgwfDA&ixlib=rb-4.1.0&q=85",
  cta: "https://images.unsplash.com/photo-1699616409199-97327373efa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxESiUyMHdlZGRpbmd8ZW58MHx8fHwxNzcxNTMxODgwfDA&ixlib=rb-4.1.0&q=85",
};

export const meetDarenContent = {
  title: "Meet Daren",
  paragraphs: [
    "Hi, I'm Daren, the DJ behind Lobben Event Co. I specialize in creating polished, high energy wedding nights with packed dance floors and natural flow from start to finish, but I also bring the same level of care, professionalism, and energy to corporate events and private celebrations.",
    "My approach is simple: take the pressure off you, handle the details behind the scenes, and deliver a night that feels effortless and unforgettable. Whether it's a wedding, company event, or private party, I focus on reading the moment, keeping the energy right, and making sure your guests are having an incredible time, while you actually get to enjoy your event.",
    "From intimate weddings to large receptions, corporate functions, and milestone celebrations, my goal is always the same: give you total peace of mind and an experience your guests will be talking about long after the night is over.",
  ],
};

export const contactInfo = {
  phone: "(720) 432 6407",
  email: "daren@lobbeneventco.com",
  instagram: "@lobbeneventco",
  instagramUrl: "https://www.instagram.com/lobbeneventco",
  facebook: "Lobben Event Co.",
  facebookUrl: "https://www.facebook.com/lobbeneventco",
  location: "Based in Denver, serving events across Colorado.",
};
